import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, MessageCircle } from 'lucide-react';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [forceCloseDropdown, setForceCloseDropdown] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const openLiveChat = () => {
    try {
      // Method 1: Use cd360Api directly
      if (typeof window.cd360Api !== 'undefined' && 
          window.cd360Api.liveChat && 
          typeof window.cd360Api.liveChat.showWebWidget === 'function') {
        window.cd360Api.liveChat.showWebWidget();
        return;
      }
      
      // Method 2: Use our custom function
      if (typeof window.opentawktrikWidget === 'function') {
        window.opentawktrikWidget();
        return;
      }
      
      // Method 3: Direct iframe interaction
      const configIframe = document.getElementById('cd360ConfigIframe');
      if (configIframe && configIframe.contentWindow) {
        configIframe.contentWindow.postMessage({
          type: 'cd360Api',
          apiType: 'liveChat',
          method: 'showWebWidget',
          message: ''
        }, '*');
        return;
      }
      
      // Method 4: Click the badge directly
      const badge = document.getElementById('badge');
      if (badge) {
        badge.click();
        return;
      }
      
      console.log('Chat widget is loading...');
    } catch (error) {
      console.error('Error opening chat:', error);
    }
  };

  // Close dropdown when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    // Force close the dropdown on route change
    setForceCloseDropdown(true);
    
    // Remove force close after page has fully loaded
    const timer = setTimeout(() => {
      setForceCloseDropdown(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [location.pathname]);

  return (
    <header className="site-header">
      <div className="header-container">
        <Link to="/" className="logo">
          <img src="/assets/img/brand/logo-dark.png" alt="Unity Trademark" className="logo-image" />
        </Link>

        <button 
          className="menu-toggle" 
          onClick={toggleMenu}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        <nav className={`main-nav ${isMenuOpen ? 'is-open' : ''}`}>
          <ul>
            <li>
              <Link 
                to="/" 
                className={isActive('/') ? 'active' : ''}
                onClick={closeMenu}
              >
                Home
              </Link>
            </li>
            <li className={`dropdown has-megamenu ${forceCloseDropdown ? 'force-close' : ''}`}>
              <span className="dropdown-trigger">
                Services
                <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" className="dropdown-arrow">
                  <path d="M6 8L2 4h8L6 8z"/>
                </svg>
              </span>
              <div className="megamenu">
                <div className="megamenu-content">
                  <Link 
                    to="/trademark-registration" 
                    className={`megamenu-item ${isActive('/trademark-registration') ? 'active' : ''}`}
                    onClick={closeMenu}
                  >
                    <div className="megamenu-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 11l3 3L22 4"></path>
                        <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"></path>
                      </svg>
                    </div>
                    <div className="megamenu-text">
                      <h4>Trademark Registration</h4>
                      <p>Protect your brand identity</p>
                    </div>
                  </Link>
                  
                  <Link 
                    to="/copyright-registration" 
                    className={`megamenu-item ${isActive('/copyright-registration') ? 'active' : ''}`}
                    onClick={closeMenu}
                  >
                    <div className="megamenu-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M14.5 9.5a3.5 3.5 0 00-5 0"></path>
                      </svg>
                    </div>
                    <div className="megamenu-text">
                      <h4>Copyright Registration</h4>
                      <p>Safeguard your creative works</p>
                    </div>
                  </Link>
                  
                  <Link 
                    to="/company-formation" 
                    className={`megamenu-item ${isActive('/company-formation') ? 'active' : ''}`}
                    onClick={closeMenu}
                  >
                    <div className="megamenu-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                        <path d="M9 3v18M15 3v18M3 9h18M3 15h18"></path>
                      </svg>
                    </div>
                    <div className="megamenu-text">
                      <h4>Company Formation</h4>
                      <p>Launch your business right</p>
                    </div>
                  </Link>
                  
                  <Link 
                    to="/trademark-search" 
                    className={`megamenu-item ${isActive('/trademark-search') ? 'active' : ''}`}
                    onClick={closeMenu}
                  >
                    <div className="megamenu-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="M21 21l-4.35-4.35"></path>
                      </svg>
                    </div>
                    <div className="megamenu-text">
                      <h4>Trademark Search</h4>
                      <p>Ensure your mark is unique</p>
                    </div>
                  </Link>
                  
                  <Link 
                    to="/amazon-brand-registry" 
                    className={`megamenu-item ${isActive('/amazon-brand-registry') ? 'active' : ''}`}
                    onClick={closeMenu}
                  >
                    <div className="megamenu-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"></path>
                      </svg>
                    </div>
                    <div className="megamenu-text">
                      <h4>Amazon Brand Registry</h4>
                      <p>Protect your Amazon brand</p>
                    </div>
                  </Link>
                </div>
              </div>
            </li>
            <li>
              <Link 
                to="/about-us" 
                className={isActive('/about-us') ? 'active' : ''}
                onClick={closeMenu}
              >
                About Us
              </Link>
            </li>
            <li>
              <Link 
                to="/contact-us" 
                className={isActive('/contact-us') ? 'active' : ''}
                onClick={closeMenu}
              >
                Contact Us
              </Link>
            </li>
          </ul>
          <button 
            className="live-chat-button"
            onClick={openLiveChat}
            aria-label="Open live chat"
          >
            <MessageCircle size={20} />
            <span>Live Chat</span>
          </button>
        </nav>
      </div>
    </header>
  );
};

export default Header;